import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

export default function ExcelUploadModal({ selectedMonth, onClose, onSuccess, type = 'main' }) {
  const [step, setStep] = useState(1); // 1 = upload, 2 = mapping, 3 = importing
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [savedMappings, setSavedMappings] = useState([]);
  const [saveMappingName, setSaveMappingName] = useState('');
  const [showSaveMappingInput, setShowSaveMappingInput] = useState(false);
  
  // Vlastní výběr měsíce a roku (defaultně současný měsíc nebo selectedMonth)
  const currentDate = new Date();
  const [uploadYear, setUploadYear] = useState(
    selectedMonth ? parseInt(selectedMonth.split('-')[0]) : currentDate.getFullYear()
  );
  const [uploadMonth, setUploadMonth] = useState(
    selectedMonth ? parseInt(selectedMonth.split('-')[1]) : currentDate.getMonth() + 1
  );
  
  // Formátovaný měsíc pro API
  const getFormattedMonth = () => {
    return `${uploadYear}-${String(uploadMonth).padStart(2, '0')}`;
  };

  // Konfigurace podle typu
  const getConfig = () => {
    switch (type) {
      case 'main':
        return {
          title: 'Nahrát Excel - Hlavní provize',
          apiPath: 'commissions',
          columns: [
            { key: 'companyName', label: 'A) Název firmy', required: true },
            { key: 'ico', label: 'B) IČO', required: true },
            { key: 'bmsl', label: 'C) BMSL', required: false },
            { key: 'hwBudget', label: 'D) HW Budget', required: false },
            { key: 'hwCoefficient', label: 'E) HW Koeficient', required: false },
            { key: 'contractLength', label: 'F) Délka smlouvy', required: false },
            { key: 'specialCoefficient', label: 'G) Speciální koeficient', required: false },
            { key: 'gp', label: 'I) GP', required: false }
          ]
        };
      case 'strategic':
        return {
          title: 'Nahrát Excel - Strategické provize',
          apiPath: 'strategic-commissions',
          columns: [
            { key: 'companyName', label: 'A) Název firmy', required: true },
            { key: 'ico', label: 'B) IČO', required: false },
            { key: 'productType', label: 'C) Typ produktu', required: true },
            { key: 'gp', label: 'D) GP', required: true }
          ]
        };
      case 'cashback-strategic':
        return {
          title: 'Nahrát Excel - Cashback strategické provize',
          apiPath: 'cashback-strategic-commissions',
          columns: [
            { key: 'companyName', label: 'A) Název firmy', required: true },
            { key: 'ico', label: 'B) IČO', required: false },
            { key: 'productType', label: 'C) Typ produktu', required: true },
            { key: 'gp', label: 'D) GP', required: true }
          ]
        };
      case 'cashback':
        return {
          title: 'Nahrát Excel - Cashback/Srážky',
          apiPath: 'cashbacks',
          columns: [
            { key: 'companyName', label: 'A) Název firmy', required: true },
            { key: 'ico', label: 'B) IČO', required: false },
            { key: 'signatureDate', label: 'C) Datum podpisu', required: false },
            { key: 'bmsl', label: 'D) BMSL', required: false },
            { key: 'hwBudget', label: 'E) HW rozpočet', required: false },
            { key: 'hwCoefficient', label: 'F) HW koeficient', required: false },
            { key: 'contractLength', label: 'G) Délka smlouvy', required: false },
            { key: 'specialCoefficient', label: 'H) Speciální koeficient', required: false },
            { key: 'gp', label: 'I) GP', required: true }
          ]
        };
      default:
        return getConfig.main;
    }
  };

  const config = getConfig();
  const requiredColumns = config.columns;

  // Nahrání a náhled souboru
  const handleFileUpload = async () => {
    if (!file) {
      alert('Prosím vyberte soubor');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/${config.apiPath}/upload/preview`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.previewData);
        setAvailableColumns(data.availableColumns);
        
        // Pokus o automatické mapování na základě názvů sloupců
        const autoMapping = {};
        requiredColumns.forEach(reqCol => {
          const matchingCol = data.availableColumns.find(col => 
            col.toLowerCase().includes(reqCol.key.toLowerCase()) ||
            col.toLowerCase().includes(reqCol.label.toLowerCase().substring(3)) // Odstraň "A) " prefix
          );
          if (matchingCol) {
            autoMapping[reqCol.key] = matchingCol;
          }
        });
        setColumnMapping(autoMapping);
        
        setStep(2);
      } else {
        const errorData = await response.json();
        alert('Chyba při načítání souboru: ' + errorData.error);
      }
    } catch (error) {
      console.error('Chyba při nahrávání souboru:', error);
      alert('Chyba při nahrávání souboru');
    }
  };

  // Import s mapováním
  const handleImport = async () => {
    // Kontrola povinných sloupců
    const missingRequired = requiredColumns
      .filter(col => col.required && !columnMapping[col.key])
      .map(col => col.label);
    
    if (missingRequired.length > 0) {
      alert('Chybí mapování pro povinné sloupce: ' + missingRequired.join(', '));
      return;
    }

    setImporting(true);
    setStep(3);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(columnMapping));
    formData.append('month', getFormattedMonth());

    try {
      const response = await fetch(`${API_URL}/${config.apiPath}/upload/import`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        
        if (result.errors === 0) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        setImportResult({
          success: false,
          error: errorData.error
        });
      }
    } catch (error) {
      console.error('Chyba při importu:', error);
      setImportResult({
        success: false,
        error: 'Chyba při importu dat'
      });
    } finally {
      setImporting(false);
    }
  };

  // Načtení uložených mapování při otevření modalu
  useEffect(() => {
    fetchSavedMappings();
  }, []);

  const fetchSavedMappings = async () => {
    try {
      console.log('🔍 Načítám uložená mapování z:', `${API_URL}/commissions/mappings`);
      const response = await fetch(`${API_URL}/commissions/mappings`);
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📋 Načtená data:', data);
        setSavedMappings(data.mappings || []);
        console.log('💾 Počet uložených mapování:', data.mappings ? data.mappings.length : 0);
      } else {
        console.error('❌ Špatná odpověď serveru:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Chyba při načítání uložených mapování:', error);
    }
  };

  // Uložit aktuální mapování
  const saveCurrentMapping = async () => {
    if (!saveMappingName.trim()) {
      alert('Prosím zadejte název pro mapování');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/commissions/mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: saveMappingName,
          mapping: columnMapping,
          userId: null // Můžeme přidat userId později
        })
      });

      if (response.ok) {
        alert('Mapování bylo uloženo');
        setSaveMappingName('');
        setShowSaveMappingInput(false);
        fetchSavedMappings(); // Znovu načti seznam
      } else {
        alert('Chyba při ukládání mapování');
      }
    } catch (error) {
      console.error('Chyba při ukládání mapování:', error);
      alert('Chyba při ukládání mapování');
    }
  };

  // Načíst uložené mapování
  const loadSavedMapping = (mapping) => {
    setColumnMapping(mapping.mapping);
    alert(`Mapování "${mapping.name}" bylo načteno`);
  };

  // Smazat uložené mapování
  const deleteSavedMapping = async (mappingId) => {
    if (!window.confirm('Opravdu chcete smazat toto mapování?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/commissions/mappings/${mappingId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Mapování bylo smazáno');
        fetchSavedMappings(); // Znovu načti seznam
      } else {
        alert('Chyba při mazání mapování');
      }
    } catch (error) {
      console.error('Chyba při mazání mapování:', error);
      alert('Chyba při mazání mapování');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold">
            {step === 1 && config.title}
            {step === 2 && 'Mapování sloupců - ' + config.title.split(' - ')[1]}
            {step === 3 && 'Import dat - ' + config.title.split(' - ')[1]}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Krok 1: Upload souboru */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Výběr měsíce a roku */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📅 Vyberte měsíc a rok pro import dat:
                </label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Měsíc</label>
                    <select
                      value={uploadMonth}
                      onChange={(e) => setUploadMonth(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={1}>Leden</option>
                      <option value={2}>Únor</option>
                      <option value={3}>Březen</option>
                      <option value={4}>Duben</option>
                      <option value={5}>Květen</option>
                      <option value={6}>Červen</option>
                      <option value={7}>Červenec</option>
                      <option value={8}>Srpen</option>
                      <option value={9}>Září</option>
                      <option value={10}>Říjen</option>
                      <option value={11}>Listopad</option>
                      <option value={12}>Prosinec</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Rok</label>
                    <select
                      value={uploadYear}
                      onChange={(e) => setUploadYear(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {[...Array(5)].map((_, i) => {
                        const year = currentDate.getFullYear() - 1 + i;
                        return <option key={year} value={year}>{year}</option>;
                      })}
                    </select>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Data budou importována pro: <strong className="text-blue-600">
                    {new Date(getFormattedMonth() + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                  </strong>
                </p>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Nahrajte Excel soubor s daty pro provize
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="text-center">
                      <div className="text-4xl text-gray-400 mb-4">📊</div>
                      <p className="text-lg font-medium text-gray-700">
                        Klikněte pro výběr Excel souboru
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Podporované formáty: .xlsx, .xls
                      </p>
                    </div>
                  </label>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-blue-50 rounded border text-left">
                    <div className="font-medium text-blue-800">Vybraný soubor:</div>
                    <div className="text-blue-600">{file.name}</div>
                    <div className="text-sm text-blue-500">
                      Velikost: {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                <h4 className="font-medium text-yellow-800 mb-2">📋 Požadované sloupce v Excel souboru:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-yellow-700">
                  {requiredColumns.map(col => (
                    <div key={col.key} className="flex items-center gap-2">
                      {col.required ? (
                        <span className="text-red-500 font-bold">*</span>
                      ) : (
                        <span className="text-gray-400">•</span>
                      )}
                      <span>{col.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-yellow-600 mt-2">
                  * Povinné sloupce | Názvy sloupců v Excelu mohou být jiné, v dalším kroku je namapujete
                </p>
              </div>
            </div>
          )}

          {/* Krok 2: Mapování sloupců */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-4">
                  Namapujte sloupce z vašeho Excel souboru na požadované pole:
                </h4>
                
                <div className="grid gap-4">
                  {requiredColumns.map(reqCol => (
                    <div key={reqCol.key} className="flex items-center gap-4 p-3 bg-gray-50 rounded">
                      <div className="w-1/3">
                        <label className="font-medium text-gray-700">
                          {reqCol.label}
                          {reqCol.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                      </div>
                      <div className="w-1/3">
                        <select
                          value={columnMapping[reqCol.key] || ''}
                          onChange={(e) => setColumnMapping({
                            ...columnMapping,
                            [reqCol.key]: e.target.value
                          })}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="">-- Vyberte sloupec --</option>
                          {availableColumns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-1/3 text-sm text-gray-600">
                        {columnMapping[reqCol.key] ? (
                          <span className="text-green-600">✓ Namapováno</span>
                        ) : reqCol.required ? (
                          <span className="text-red-500">Povinné</span>
                        ) : (
                          <span className="text-gray-400">Volitelné</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ukládání a načítání mapování */}
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-3">💾 Uložená mapování</h4>
                
                {/* Uložit aktuální mapování */}
                <div className="mb-4">
                  {!showSaveMappingInput ? (
                    <button
                      onClick={() => setShowSaveMappingInput(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      Uložit aktuální mapování
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Název mapování..."
                        value={saveMappingName}
                        onChange={(e) => setSaveMappingName(e.target.value)}
                        className="flex-1 border rounded px-3 py-2 text-sm"
                      />
                      <button
                        onClick={saveCurrentMapping}
                        className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                      >
                        Uložit
                      </button>
                      <button
                        onClick={() => {
                          setShowSaveMappingInput(false);
                          setSaveMappingName('');
                        }}
                        className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600 text-sm"
                      >
                        Zrušit
                      </button>
                    </div>
                  )}
                </div>

                {/* Seznam uložených mapování */}
                {savedMappings.length > 0 && (
                  <div>
                    <p className="text-sm text-blue-700 mb-2">Dostupná mapování:</p>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {savedMappings.map(mapping => (
                        <div key={mapping._id} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                          <span className="font-medium">{mapping.name}</span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => loadSavedMapping(mapping)}
                              className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs"
                            >
                              Načíst
                            </button>
                            <button
                              onClick={() => deleteSavedMapping(mapping._id)}
                              className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                            >
                              Smazat
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {savedMappings.length === 0 && (
                  <p className="text-sm text-blue-600">Zatím nemáte žádná uložená mapování.</p>
                )}
              </div>

              {/* Náhled dat */}
              {previewData && previewData.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Náhled dat (prvních 5 řádků):</h4>
                  <div className="overflow-x-auto border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {availableColumns.map(col => (
                            <th key={col} className="px-3 py-2 text-left font-medium">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, i) => (
                          <tr key={i} className="border-t">
                            {availableColumns.map(col => (
                              <td key={col} className="px-3 py-2">{row[col]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Krok 3: Import */}
          {step === 3 && (
            <div className="text-center space-y-6">
              {importing ? (
                <div>
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-lg">Importuji data...</p>
                  <p className="text-sm text-gray-600">Prosím čekejte, import může trvat několik sekund</p>
                </div>
              ) : importResult ? (
                <div>
                  {importResult.success ? (
                    <div className="bg-green-50 p-6 rounded border border-green-200">
                      <div className="text-4xl text-green-500 mb-4">✅</div>
                      <h4 className="text-lg font-bold text-green-800 mb-2">Import dokončen!</h4>
                      <div className="space-y-2 text-green-700">
                        <p>Importováno: <strong>{importResult.imported}</strong> provizí</p>
                        <p>Měsíc: <strong>{new Date(importResult.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}</strong></p>
                        {importResult.errors > 0 && (
                          <p className="text-orange-600">
                            Varování: {importResult.errors} řádků se nepodařilo importovat
                          </p>
                        )}
                      </div>
                      <p className="text-sm text-green-600 mt-4">
                        Stránka se automaticky obnoví...
                      </p>
                    </div>
                  ) : (
                    <div className="bg-red-50 p-6 rounded border border-red-200">
                      <div className="text-4xl text-red-500 mb-4">❌</div>
                      <h4 className="text-lg font-bold text-red-800 mb-2">Chyba při importu</h4>
                      <p className="text-red-700">{importResult.error}</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div>
            {step > 1 && !importing && (
              <button
                onClick={() => setStep(step - 1)}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                ← Zpět
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              disabled={importing}
            >
              {step === 3 && importResult && importResult.success ? 'Zavřít' : 'Zrušit'}
            </button>
            
            {step === 1 && (
              <button
                onClick={handleFileUpload}
                disabled={!file}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Nahrát a pokračovat →
              </button>
            )}
            
            {step === 2 && (
              <button
                onClick={handleImport}
                disabled={!columnMapping.companyName || !columnMapping.ico}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Importovat data →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}